import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Ban,
  CheckCircle2,
  Download,
  FileCog,
  Inbox,
  Send,
  Trash2,
  Wallet,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { EmptyState, PageHeader, SectionCard, TableShell, Th } from "../components/ui";
import { MpzpCoverageBadge } from "../components/mpzp/MpzpCoverageBadge";
import { MpzpOrderDataFixBar } from "../components/mpzp/MpzpOrderDataFixBar";
import { MpzpPoaFeeModal } from "../components/mpzp/MpzpPoaFeeModal";
import { MpzpRequestFeeModal } from "../components/mpzp/MpzpRequestFeeModal";
import { EditDooModal } from "../components/DooFeeAttach";
import { ResultFilesUpload } from "../components/ResultFilesUpload";
import { OrderHistorySection } from "../components/orders/OrderHistorySection";
import { OrderOrdererControl } from "../components/orders/OrderOrdererControl";
import { OrderRevertControl } from "../components/orders/OrderRevertControl";
import { MpzpStatusBadge } from "../components/mpzp/MpzpStatusBadge";
import { MpzpTimeline } from "../components/mpzp/MpzpTimeline";
import { useMpzpOrder, useMpzpOrderMutations, useTaskPolling } from "../hooks";
import { fmtDate, fmtDateTime } from "../utils";
import type { ManagedFileMini } from "../types";
import { MPZP_ORDER_STATUS_LABELS, WYPIS_APPLICANT_LABELS } from "../types";

const FEE_STATUS_LABELS: Record<string, string> = {
  pending_approval: "Oczekuje na akceptację",
  approved: "Zaakceptowana — do opłacenia",
  rejected: "Odrzucona",
  payment_uploaded: "Opłacona",
  acknowledged: "Opłacona i potwierdzona",
  withdrawn: "Wycofana",
};

const CLOSED = ["sent", "received", "completed", "cancelled"];

function FileLink({ file, label }: { file: ManagedFileMini | null; label: string }) {
  if (!file?.url) return null;
  return (
    <a
      href={file.url}
      download={file.original_filename}
      className="inline-flex items-center gap-1 text-xs font-mono border border-[var(--color-border-strong)] px-2 py-1 hover:bg-[var(--color-bg-elevated)]"
    >
      <Download size={12} /> {label}
    </a>
  );
}

export function MpzpOrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const { data: order, isLoading } = useMpzpOrder(orderId);
  const m = useMpzpOrderMutations(orderId);
  const navigate = useNavigate();
  const { data: internalUsers } = useInternalUsers();
  const contactOptions = useMemo(
    () =>
      (internalUsers ?? []).map((u) => ({ value: String(u.id), label: u.full_name || u.username })),
    [internalUsers],
  );

  const [generateTaskId, setGenerateTaskId] = useState<string | null>(null);
  const generateTask = useTaskPolling(generateTaskId);
  const [feeModal, setFeeModal] = useState(false);
  const [editDooModal, setEditDooModal] = useState(false);
  const [poaFeeModal, setPoaFeeModal] = useState(false);
  const [actionError, setActionError] = useState("");
  // Wpis ręczny kasuje się w całości (nie ma opłat ani korespondencji) — po potwierdzeniu.
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Zakończone zadanie generacji zmieniło wiersz zamówienia — dociągnij pliki.
  useEffect(() => {
    const done = (s?: string) => s && ["success", "failure", "cancelled"].includes(s);
    if (generateTaskId && done(generateTask.data?.status)) {
      qc.invalidateQueries({ queryKey: ["pp", "mpzp-order", orderId] });
    }
  }, [generateTask.data?.status, generateTaskId, orderId, qc]);

  // Lustro bramki backendu: autor, jego następca (osoba do kontaktów po nieaktywnym
  // autorze), dyrektor, CEO — nie zgadujemy tego z `created_by`.
  const canManage = !!order?.can_manage;

  if (isLoading || !order) {
    return <div className="py-16 flex justify-center"><Spinner /></div>;
  }

  const generating =
    !!generateTaskId &&
    !["success", "failure", "cancelled"].includes(generateTask.data?.status ?? "");

  const runAction = (fn: () => void) => { setActionError(""); fn(); };
  const onActionError = (e: unknown) => {
    const detail = (e as { response?: { data?: { errors?: { detail?: unknown } } } })?.response
      ?.data?.errors?.detail;
    setActionError(typeof detail === "string" ? detail : "Operacja nie powiodła się.");
  };

  const gaps = order.data_gaps;
  // Wypis pozyskany ręcznie poza ELGW5: bez wniosku, opłat i wysyłki — zostają działki,
  // pliki, uwagi, zakończenie i usunięcie wpisu.
  const manual = order.obtained_manually;

  const parcelsCard = (
    <SectionCard title={`${manual ? "Działki" : "Działki wniosku"} (${order.parcels_detail.length})`}>
      <TableShell>
        <thead>
          <tr>
            <Th>Nr działki</Th>
            <Th>Obręb</Th>
            <Th>Arkusz</Th>
            <Th>Miejscowość</Th>
            <Th>Ulica i numer</Th>
            <Th>Plan</Th>
          </tr>
        </thead>
        <tbody>
          {order.parcels_detail.map((p) => (
            <tr key={p.id} className="border-t border-[var(--color-border)]">
              <td className="px-2 py-1 font-mono" title={p.teryt}>
                {p.short_number || p.teryt}
              </td>
              <td className="px-2 py-1">{p.obreb || "—"}</td>
              <td className="px-2 py-1">{p.karta_mapy || "—"}</td>
              <td className="px-2 py-1">
                {p.locality || (
                  <span className="text-[var(--color-text-muted)]">
                    {p.obreb ? `obr. ${p.obreb}` : "—"}
                  </span>
                )}
              </td>
              <td className="px-2 py-1">{p.street_and_number || "—"}</td>
              <td className="px-2 py-1">
                <MpzpCoverageBadge status={p.mpzp_status} label={p.mpzp_status_label} />
              </td>
            </tr>
          ))}
        </tbody>
      </TableShell>
    </SectionCard>
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {order.number} <MpzpStatusBadge status={order.status} />
          </span>
        }
        subtitle={
          manual
            ? `Gmina ${order.commune || "—"} · pozyskany ręcznie poza ELGW5 · ${order.parcel_count} dz.`
            : `Gmina ${order.commune || "—"} · ${order.office_name || "brak adresata"} · ${order.scope_label} · ${order.parcel_count} dz. · wnioskodawca: ${WYPIS_APPLICANT_LABELS[order.applicant] ?? "ELEKTIM"}`
        }
        backTo="/power-projects/zamawianie?tab=mpzp"
        backLabel="Wypisy MPZP"
        actions={
          manual && canManage ? (
            confirmDelete ? (
              <span className="inline-flex items-center gap-2 text-xs">
                Usunąć wpis razem z listą plików?
                <Button
                  variant="danger"
                  size="sm"
                  loading={m.remove.isPending}
                  onClick={() =>
                    runAction(() =>
                      m.remove.mutate(order.id, {
                        onSuccess: () => navigate("/power-projects/zamawianie?tab=mpzp"),
                        onError: onActionError,
                      }),
                    )
                  }
                >
                  <Trash2 size={13} className="mr-1" /> Usuń
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(false)}>
                  Zostaw
                </Button>
              </span>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                title="Wpis ręczny nie ma opłat ani korespondencji — kasuje się w całości (same pliki zostają w historii)"
              >
                <Trash2 size={13} className="mr-1" /> Usuń wpis
              </Button>
            )
          ) : canManage && order.status === "new" ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => runAction(() => m.cancel.mutate(order.id, { onError: onActionError }))}
            >
              <Ban size={13} className="mr-1" /> Anuluj zamówienie
            </Button>
          ) : undefined
        }
      />

      <MpzpTimeline order={order} />
      {manual && (
        <div className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2 text-xs text-[var(--color-text-secondary)]">
          Wypis i wyrys <b>pozyskany ręcznie poza ELGW5</b> — dodany z karty działki samymi
          plikami, dlatego nie ma tu wniosku, opłat ani wysyłki. Można dokładać pliki,
          zakończyć sprawę albo usunąć wpis.
        </div>
      )}
      <OrderOrdererControl
        ordererId={order.created_by}
        ordererName={order.created_by_name}
        ordererPhone={order.created_by_phone}
        ordererEmail={order.created_by_email}
        takenOverByName={order.taken_over_by_name}
        takenOverByPhone={order.taken_over_by_phone}
        takenOverByEmail={order.taken_over_by_email}
        canManage={canManage}
        pending={m.changeOrderer.isPending}
        onChange={(userId) =>
          runAction(() =>
            m.changeOrderer.mutate({ id: order.id, userId }, { onError: onActionError }),
          )
        }
      />
      {/* Cofanie etapów — cel wyznacza backend; przy „zakończonym" to jedyna droga
          z powrotem, bo status zakończony jest terminalny w biegu naprzód. */}
      {canManage && (
        <OrderRevertControl
          status={order.status}
          revertTo={order.revert_to}
          revertLabel={order.revert_label}
          statusLabels={MPZP_ORDER_STATUS_LABELS}
          pending={m.revert.isPending}
          onRevert={(reason) =>
            runAction(() =>
              m.revert.mutate({ id: order.id, reason }, { onError: onActionError }),
            )
          }
        />
      )}
      {actionError && <div className="text-xs text-red-600">{actionError}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <div className="space-y-4">
          {manual && parcelsCard}
          {!manual && (
            <>
          {/* ── dokumenty ── */}
          <SectionCard
            title="Dokumenty wniosku"
            action={
              !CLOSED.includes(order.status) ? (
                <Button
                  size="sm"
                  loading={generating || m.generate.isPending}
                  onClick={() =>
                    runAction(() =>
                      m.generate.mutate(order.id, {
                        onSuccess: (r) => setGenerateTaskId(r.task_id),
                        onError: onActionError,
                      }),
                    )
                  }
                >
                  <FileCog size={13} className="mr-1" />
                  {order.has_documents ? "Generuj ponownie" : "Generuj wniosek"}
                </Button>
              ) : undefined
            }
          >
            <div className="mb-3 text-xs text-[var(--color-text-secondary)]">
              Wniosek powstaje z edytowalnego szablonu ODT
              {order.template_name ? <> (<b>{order.template_name}</b>)</> : null}. Dane działek
              wchodzą tabelą zaprojektowaną pod A4 pionowe.{" "}
              <Link to="/doc-generation" className="text-[var(--color-primary)] hover:underline">
                Wzór podmienisz w „Generacji dokumentów” →
              </Link>
            </div>
            {(gaps.missing_obreb > 0 || gaps.missing_address > 0) && (
              <div className="mb-3 border border-amber-300 bg-amber-50/60 p-2 text-[11px] text-amber-900">
                Niekompletne dane działek:{" "}
                {[
                  gaps.missing_obreb ? `${gaps.missing_obreb} bez obrębu` : "",
                  gaps.missing_address ? `${gaps.missing_address} bez miejscowości i adresu` : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
                . W tabeli wniosku wyjdą jako „—” albo „obr. …”.
                <MpzpOrderDataFixBar order={order} />
              </div>
            )}
            {!CLOSED.includes(order.status) && (
              <label className="block mb-3 max-w-sm">
                <span className="section-label">Osoba do kontaktu</span>
                <SearchableSelect
                  options={contactOptions}
                  value={order.contact_person ?? (me ? String(me.id) : "")}
                  placeholder="Wybierz osobę…"
                  onChange={(e) =>
                    m.update.mutate({ id: order.id, payload: { contact_person: e.target.value } })
                  }
                />
                {order.has_documents && (
                  <span className="text-[11px] text-[var(--color-text-muted)]">
                    Po zmianie wygeneruj wniosek ponownie.
                  </span>
                )}
              </label>
            )}
            {generating ? (
              <div className="flex items-center gap-2 text-xs py-2">
                <Spinner size="sm" /> Generuję wniosek…
              </div>
            ) : order.has_documents ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <FileLink file={order.form_file} label="Wniosek (ODT)" />
                  <FileLink file={order.form_file_pdf} label="Wniosek (PDF)" />
                  <FileLink file={order.attachment_xlsx} label="Lista działek (XLSX)" />
                </div>
                <div className="text-[11px] text-[var(--color-text-muted)]">
                  Wygenerowano {fmtDateTime(order.generated_at)} — sprawdź treść przed wysyłką.
                </div>
              </div>
            ) : (
              <EmptyState>
                {generateTask.data?.status === "failure"
                  ? "Generacja nie powiodła się — sprawdź, czy jest wgrany szablon wniosku, i spróbuj ponownie."
                  : "Brak wygenerowanego wniosku"}
              </EmptyState>
            )}
          </SectionCard>

          {/* ── opłata za pełnomocnictwo ── */}
          <SectionCard
            title="Opłata za pełnomocnictwo (17 zł)"
            action={
              canManage && !CLOSED.includes(order.status) ? (
                <Button size="sm" variant="secondary" onClick={() => setPoaFeeModal(true)}>
                  <Wallet size={13} className="mr-1" />
                  {order.poa_fees.length > 0
                    ? "Dodaj kolejną"
                    : order.poa_fee_not_required
                      ? "Zmień"
                      : "Rozlicz"}
                </Button>
              ) : undefined
            }
          >
            {order.poa_fee_not_required ? (
              <div className="text-xs text-[var(--color-text-secondary)]">
                Oznaczona jako niepotrzebna
                {order.poa_fee_not_required_reason
                  ? ` — ${order.poa_fee_not_required_reason}`
                  : ""}
                .
              </div>
            ) : order.poa_fees.length > 0 ? (
              <div className="space-y-2 text-xs">
                {order.poa_fees.map((poaFee) => (
                  <div key={poaFee.id} className="flex items-center gap-2 flex-wrap">
                    <Badge
                      color={
                        poaFee.status === "payment_uploaded" || poaFee.status === "acknowledged"
                          ? "green"
                          : poaFee.status === "rejected" || poaFee.status === "withdrawn"
                            ? "red"
                            : "yellow"
                      }
                    >
                      {FEE_STATUS_LABELS[poaFee.status] ?? poaFee.status}
                    </Badge>
                    <span className="font-mono">{poaFee.amount} PLN</span>
                    <span className="text-[var(--color-text-secondary)]">{poaFee.office_name}</span>
                    <Link
                      to={`/official-fees/${poaFee.id}`}
                      className="hover:underline text-[var(--color-primary)]"
                    >
                      otwórz →
                    </Link>
                    {canManage && !CLOSED.includes(order.status) && (
                      <button
                        className="ml-auto text-[var(--color-text-muted)] hover:text-red-600"
                        title="Odepnij opłatę od zamówienia (pozostaje w /official-fees)"
                        onClick={() =>
                          runAction(() =>
                            m.setPoaFee.mutate(
                              { id: order.id, payload: { mode: "detach", fee: poaFee.id } },
                              { onError: onActionError },
                            ),
                          )
                        }
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState>
                Wymagana domyślnie (ELEKTIM jako pełnomocnik inwestora) — podepnij/poproś o opłatę
                albo oznacz jako niepotrzebną przed wysyłką. Idzie do tego samego urzędu, co
                wniosek.
              </EmptyState>
            )}
          </SectionCard>

          {/* ── opłata ── */}
          <SectionCard
            title="Opłata skarbowa za wypis i wyrys"
            action={
              // „sent" tak samo jak „new" — kwotę wylicza gmina, więc pismo o opłacie
              // przychodzi zwykle dopiero po złożeniu wniosku.
              canManage && order.status !== "cancelled" ? (
                <div className="flex items-center gap-2">
                  {(order.fee_id || order.doo_file) && (
                    <Button size="sm" variant="secondary" onClick={() => setEditDooModal(true)}>
                      Edytuj DOO
                    </Button>
                  )}
                  {["new", "sent"].includes(order.status) && (
                    <Button size="sm" onClick={() => setFeeModal(true)}>
                      <Wallet size={13} className="mr-1" />
                      {order.fee_id ? "Złóż ponownie" : "Dodaj opłatę"}
                    </Button>
                  )}
                </div>
              ) : undefined
            }
          >
            {order.fee_id ? (
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    color={
                      order.fee_status === "payment_uploaded" || order.fee_status === "acknowledged"
                        ? "green"
                        : order.fee_status === "rejected" || order.fee_status === "withdrawn"
                          ? "red"
                          : "yellow"
                    }
                  >
                    {FEE_STATUS_LABELS[order.fee_status ?? ""] ?? order.fee_status}
                  </Badge>
                  {order.fee_amount && <span className="font-mono">{order.fee_amount} PLN</span>}
                  <Link
                    to={`/official-fees/${order.fee_id}`}
                    className="hover:underline text-[var(--color-primary)]"
                  >
                    otwórz w /official-fees →
                  </Link>
                </div>
                {order.fee_rejection_comment && (
                  <div className="text-red-600">
                    Powód odrzucenia: {order.fee_rejection_comment}
                  </div>
                )}
              </div>
            ) : (
              <EmptyState>
                Kwoty nie znamy z góry — wylicza ją gmina (liczba stron wypisu i arkuszy wyrysu).
                Gdy przyśle pismo, kliknij „Dodaj opłatę” i wgraj je: kwota, konto i tytuł
                uzupełnią się automatycznie.
              </EmptyState>
            )}
            {order.doo_file?.url && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-[var(--color-text-secondary)]">Pismo o opłacie:</span>
                <a
                  href={order.doo_file.url}
                  download={order.doo_file.original_filename}
                  className="inline-flex items-center gap-1 hover:underline text-[var(--color-primary)]"
                >
                  <Download size={12} /> {order.doo_file.original_filename}
                </a>
              </div>
            )}
          </SectionCard>
            </>
          )}
        </div>

        <div className="space-y-4">
          {!manual && parcelsCard}

          {/* ── wysyłka / odbiór ── */}
          <SectionCard title={manual ? "Odbiór i zakończenie" : "Wysyłka i odbiór"}>
            <div className="space-y-2 text-xs">
              {["new", "fee_paid"].includes(order.status) && canManage && (
                <Button
                  size="sm"
                  onClick={() =>
                    runAction(() => m.markSent.mutate(order.id, { onError: onActionError }))
                  }
                  loading={m.markSent.isPending}
                >
                  <Send size={13} className="mr-1" /> Oznacz: wysłano do gminy
                </Button>
              )}
              {order.sent_at && (
                <div>Wysłano {fmtDateTime(order.sent_at)} ({order.sent_by_name})</div>
              )}
              {order.status === "sent" && canManage && (
                <Button
                  size="sm"
                  onClick={() =>
                    runAction(() => m.markReceived.mutate(order.id, { onError: onActionError }))
                  }
                  loading={m.markReceived.isPending}
                >
                  <Inbox size={13} className="mr-1" /> Oznacz: wypis i wyrys przyszedł
                </Button>
              )}
              {order.received_at && (
                <div>
                  {manual ? "Pozyskany ręcznie — dodano" : "Otrzymano"}{" "}
                  {fmtDateTime(order.received_at)} ({order.received_by_name})
                </div>
              )}
              {order.status === "received" && canManage && (
                <Button
                  size="sm"
                  onClick={() =>
                    runAction(() => m.complete.mutate(order.id, { onError: onActionError }))
                  }
                  loading={m.complete.isPending}
                >
                  <CheckCircle2 size={13} className="mr-1" /> Zakończ zamówienie
                </Button>
              )}
            </div>
          </SectionCard>

          {/* ── pliki z gminy ── */}
          <SectionCard title={`${manual ? "Pliki wypisu" : "Pliki z gminy"} (${order.result_files.length})`}>
            <div className="space-y-2">
              {/* Także na zamówieniu zakończonym — gmina bywa, że dosyła wypis albo
                  wyrys osobną przesyłką już po zamknięciu sprawy. Status zostaje
                  „Zakończone", nic się nie cofa. */}
              {["sent", "received", "completed"].includes(order.status) && canManage && (
                <ResultFilesUpload
                  kinds={[
                    { key: "wypis_wyrys", label: "Wypis / wyrys (PDF/JPG/PNG/TIFF/ZIP)", accept: ".pdf,.jpg,.jpeg,.png,.tif,.tiff,.zip,.7z,.rar", multiple: true },
                    { key: "inne", label: "Inne pliki (dowolny typ)", multiple: true, other: true },
                  ]}
                  pending={m.uploadResultFiles.isPending}
                  onUpload={(files, other, done) =>
                    runAction(() =>
                      m.uploadResultFiles.mutate(
                        { id: order.id, files, other },
                        { onSuccess: done, onError: onActionError },
                      ),
                    )
                  }
                />
              )}
              {order.result_files.length === 0 ? (
                <EmptyState>
                  {manual
                    ? "Brak plików — wgraj wypis, wyrys albo zaświadczenie, albo usuń wpis, jeśli był omyłkowy."
                    : "Brak plików — wgraj wypis, wyrys albo zaświadczenie o braku planu miejscowego."}
                </EmptyState>
              ) : (
                <div className="space-y-1">
                  {order.result_files.map((rf) => (
                    <div key={rf.id} className="flex items-center gap-2 text-xs">
                      <a
                        href={rf.file.url ?? "#"}
                        download={rf.file.original_filename}
                        className="font-mono hover:underline truncate"
                      >
                        {rf.file.original_filename}
                      </a>
                      <span className="text-[var(--color-text-muted)]">
                        {fmtDate(rf.created_at)}
                      </span>
                      {/* Usuwać wolno też na zamówieniu zakończonym — skasowanie
                          OSTATNIEGO pliku cofa status do „Wysłano" (backend); wpis
                          ręczny nie ma dokąd wracać, więc zostaje „otrzymany". */}
                      {canManage && (
                        <button
                          className="ml-auto text-[var(--color-text-muted)] hover:text-red-600"
                          title={
                            manual
                              ? "Usuń plik (plik zostaje do pobrania w historii wpisu)"
                              : "Usuń plik — skasowanie ostatniego pliku z gminy cofnie status zamówienia do „Wysłano do gminy”"
                          }
                          onClick={() =>
                            runAction(() =>
                              m.deleteResultFile.mutate(
                                { id: order.id, resultFileId: rf.id },
                                { onError: onActionError },
                              ),
                            )
                          }
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── inne informacje ── */}
          <SectionCard title={manual ? "Uwagi" : "Inne informacje (w treści wniosku)"}>
            {order.notes ? (
              <div className="text-xs whitespace-pre-wrap">{order.notes}</div>
            ) : (
              <EmptyState>Brak</EmptyState>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ── historia (pełna szerokość — wpisy bywają długie) ── */}
      <OrderHistorySection events={order.history ?? []} />

      {feeModal && <MpzpRequestFeeModal order={order} onClose={() => setFeeModal(false)} />}
      {editDooModal && (
        <EditDooModal
          module="mpzp"
          orderNumber={order.number}
          currentFee={
            order.fee_id
              ? { id: order.fee_id, amount: order.fee_amount, status: order.fee_status }
              : null
          }
          currentDooFilename={order.doo_file?.original_filename}
          pending={m.editDoo.isPending}
          onClose={() => setEditDooModal(false)}
          onSubmit={(payload) =>
            runAction(() =>
              m.editDoo.mutate(
                { id: order.id, payload },
                { onSuccess: () => setEditDooModal(false), onError: onActionError },
              ),
            )
          }
          onRequestNew={
            ["new", "sent"].includes(order.status)
              ? () => { setEditDooModal(false); setFeeModal(true); }
              : undefined
          }
        />
      )}
      {poaFeeModal && (
        <MpzpPoaFeeModal
          order={order}
          pending={m.setPoaFee.isPending}
          onClose={() => setPoaFeeModal(false)}
          onSubmit={(payload) =>
            runAction(() =>
              m.setPoaFee.mutate(
                { id: order.id, payload },
                { onSuccess: () => setPoaFeeModal(false), onError: onActionError },
              ),
            )
          }
        />
      )}
    </div>
  );
}
